import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, BuildingIcon, DatabaseIcon, XIcon, TrendingUpIcon, SparklesIcon, XCircleIcon } from 'lucide-react';
import { seedData } from '../data/seedData';
import { smartSearch, searchSpaces } from '../lib/searchIntelligence';
import { useUser } from '../lib/userContext';
import { motion, AnimatePresence } from 'framer-motion';
interface SearchDropdownProps {
  onSelectAccount: (accountId: string) => void;
  onSelectSpace: (spaceId: string) => void;
}
export function SearchDropdown({
  onSelectAccount,
  onSelectSpace
}: SearchDropdownProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    role,
    ownedAccountIds
  } = useUser();
  const accountResults = smartSearch(query, seedData.accounts, role, ownedAccountIds);
  const spaceResults = searchSpaces(query, seedData.accounts);
  const allResults = [...accountResults, ...spaceResults];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (allResults[selectedIndex]) {
          handleSelect(allResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };
  const handleSelect = (result: any) => {
    if (result.type === 'account') {
      onSelectAccount(result.id);
    } else {
      onSelectSpace(result.id);
    }
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleClear = () => {
    setQuery('');
    setSelectedIndex(0);
    inputRef.current?.focus();
  };
  const highlightMatch = (text: string, tokens: string[]) => {
    if (tokens.length === 0) return text;
    let highlighted = text;
    tokens.forEach(token => {
      const regex = new RegExp(`(${token})`, 'gi');
      highlighted = highlighted.replace(regex, '<span class="text-blue-400 font-semibold bg-blue-500/10 px-0.5 rounded">$1</span>');
    });
    return highlighted;
  };
  return <div ref={dropdownRef} className="relative w-full" data-id="element-1238">
      {/* Refined Search Input */}
      <div className="relative group" data-id="element-1239">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10" data-id="element-1240">
          <SearchIcon className="w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors duration-200" data-id="element-1241" />
        </div>
        <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown} placeholder="Search accounts, spaces, or people..." className="w-full pl-12 pr-11 py-3 bg-slate-900/40 border border-slate-800/40 hover:border-slate-700/60 focus:border-blue-500/50 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all backdrop-blur-xl shadow-lg shadow-black/10 hover:shadow-black/20 focus:shadow-xl focus:shadow-blue-500/10" data-id="element-1242" />
        {query && <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-white hover:bg-slate-700/60 rounded-lg transition-all hover:scale-105 active:scale-95 z-10" data-id="element-1243">
            <XIcon className="w-4 h-4" data-id="element-1244" />
          </button>}
      </div>
      {/* Enhanced Dropdown Results */}
      <AnimatePresence data-id="element-1245">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.98
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className="absolute top-full left-0 right-0 mt-2 bg-slate-950/95 backdrop-blur-2xl border border-slate-800/60 rounded-xl shadow-2xl shadow-black/40 overflow-hidden z-50 flex flex-col max-h-[520px]" data-id="element-1246">
            {/* Content wrapper with proper scroll */}
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-1247">
              {/* Empty State */}
              {!query && <div className="p-8 text-center" data-id="element-1248">
                  <div className="w-16 h-16 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-1249">
                    <SearchIcon className="w-8 h-8 text-slate-500" data-id="element-1250" />
                  </div>
                  <p className="text-sm text-slate-400 mb-2 font-medium" data-id="element-1251">
                    Start typing to search
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-1252">
                    Search across accounts, people, orders, and knowledge spaces
                  </p>
                </div>}
              {/* No Results */}
              {query && allResults.length === 0 && <div className="p-8 text-center" data-id="element-1253">
                  <div className="w-16 h-16 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-1254">
                    <XCircleIcon className="w-8 h-8 text-slate-500" data-id="element-1255" />
                  </div>
                  <p className="text-sm text-slate-400 mb-2 font-medium" data-id="element-1256">
                    No results found
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-1257">
                    Try adjusting your search terms
                  </p>
                </div>}
              {/* Results */}
              {query && allResults.length > 0 && <div className="py-2" data-id="element-1258">
                  {/* Account Results Section */}
                  {accountResults.length > 0 && <div data-id="element-1259">
                      <div className="px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider bg-slate-950/80 border-b border-slate-800/60 flex items-center gap-2.5 sticky top-0 backdrop-blur-sm z-10" data-id="element-1260">
                        <div className="w-5 h-5 rounded-lg bg-blue-500/10 flex items-center justify-center" data-id="element-1261">
                          <BuildingIcon className="w-3 h-3 text-blue-400" data-id="element-1262" />
                        </div>
                        <span data-id="element-1263">Accounts</span>
                        <span className="ml-auto px-2 py-0.5 bg-slate-800/60 text-slate-400 rounded-md text-[10px] font-semibold" data-id="element-1264">
                          {accountResults.length}
                        </span>
                      </div>
                      <div className="px-2 py-2 space-y-1" data-id="element-1265">
                        {accountResults.slice(0, 5).map((result, index) => {
                  const globalIndex = index;
                  const isSelected = selectedIndex === globalIndex;
                  return <button key={result.id} onClick={() => handleSelect(result)} className={`w-full px-4 py-3.5 text-left transition-all group rounded-xl ${isSelected ? 'bg-gradient-to-r from-blue-600/20 via-blue-500/10 to-transparent border border-blue-500/40 shadow-lg shadow-blue-500/10' : 'hover:bg-slate-800/60 border border-transparent hover:border-slate-700/50'}`} data-id="element-1266">
                              <div className="flex items-center gap-3.5" data-id="element-1267">
                                <div className={`w-11 h-11 rounded-xl bg-white/95 p-2.5 flex items-center justify-center shadow-lg ring-2 transition-all ${isSelected ? 'ring-blue-500/40 scale-105' : 'ring-slate-700/30 group-hover:ring-slate-600/40 group-hover:scale-105'}`} data-id="element-1268">
                                  <BuildingIcon className="w-5 h-5 text-slate-700" data-id="element-1269" />
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-1270">
                                  <div className={`text-sm font-semibold transition-colors mb-1 ${isSelected ? 'text-blue-400' : 'text-white group-hover:text-blue-400'}`} dangerouslySetInnerHTML={{
                          __html: highlightMatch(result.name, result.matchedTokens)
                        }} data-id="element-1271" />
                                  <div className="text-xs text-slate-400 truncate" data-id="element-1272">
                                    {result.subtitle}
                                  </div>
                                </div>
                                {result.relevanceScore > 90 && <div className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/30 rounded-lg text-[10px] text-blue-400 font-bold uppercase tracking-wider shadow-lg shadow-blue-500/10" data-id="element-1273">
                                    Best Match
                                  </div>}
                              </div>
                            </button>;
                })}
                      </div>
                    </div>}
                  {/* Space Results Section */}
                  {spaceResults.length > 0 && <div className={accountResults.length > 0 ? 'mt-2 border-t border-slate-800/60' : ''} data-id="element-1274">
                      <div className="px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider bg-slate-950/80 border-b border-slate-800/60 flex items-center gap-2.5 sticky top-0 backdrop-blur-sm z-10" data-id="element-1275">
                        <div className="w-5 h-5 rounded-lg bg-purple-500/10 flex items-center justify-center" data-id="element-1276">
                          <DatabaseIcon className="w-3 h-3 text-purple-400" data-id="element-1277" />
                        </div>
                        <span data-id="element-1278">Knowledge Spaces</span>
                        <span className="ml-auto px-2 py-0.5 bg-slate-800/60 text-slate-400 rounded-md text-[10px] font-semibold" data-id="element-1279">
                          {spaceResults.length}
                        </span>
                      </div>
                      <div className="px-2 py-2 space-y-1" data-id="element-1280">
                        {spaceResults.slice(0, 5).map((result, index) => {
                  const globalIndex = accountResults.length + index;
                  const isSelected = selectedIndex === globalIndex;
                  return <button key={result.id} onClick={() => handleSelect(result)} className={`w-full px-4 py-3.5 text-left transition-all group rounded-xl ${isSelected ? 'bg-gradient-to-r from-purple-600/20 via-purple-500/10 to-transparent border border-purple-500/40 shadow-lg shadow-purple-500/10' : 'hover:bg-slate-800/60 border border-transparent hover:border-slate-700/50'}`} data-id="element-1281">
                              <div className="flex items-center gap-3.5" data-id="element-1282">
                                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center shadow-lg ring-2 transition-all ${isSelected ? 'ring-purple-500/40 scale-105' : 'ring-purple-500/20 group-hover:ring-purple-500/30 group-hover:scale-105'}`} data-id="element-1283">
                                  <DatabaseIcon className="w-5 h-5 text-white" data-id="element-1284" />
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-1285">
                                  <div className={`text-sm font-semibold transition-colors mb-1 ${isSelected ? 'text-purple-400' : 'text-white group-hover:text-purple-400'}`} dangerouslySetInnerHTML={{
                          __html: highlightMatch(result.name, result.matchedTokens)
                        }} data-id="element-1286" />
                                  <div className="text-xs text-slate-400 truncate" data-id="element-1287">
                                    {result.subtitle}
                                  </div>
                                </div>
                              </div>
                            </button>;
                })}
                      </div>
                    </div>}
                </div>}
            </div>
            {/* Footer - Fixed at bottom */}
            {query && allResults.length > 0 && <div className="flex-shrink-0 px-5 py-4 border-t border-slate-800/60 bg-slate-950/80 backdrop-blur-sm" data-id="element-1288">
                <div className="flex items-center justify-between text-xs" data-id="element-1289">
                  <span className="text-slate-500 font-medium" data-id="element-1290">
                    {allResults.length} result
                    {allResults.length !== 1 ? 's' : ''} found
                  </span>
                  <div className="flex items-center gap-2 text-slate-500" data-id="element-1291">
                    <kbd className="px-2 py-1 bg-slate-800/60 border border-slate-700/50 rounded text-xs font-mono" data-id="element-1292">
                      ↑↓
                    </kbd>
                    <span data-id="element-1293">to navigate</span>
                    <kbd className="px-2 py-1 bg-slate-800/60 border border-slate-700/50 rounded text-xs font-mono" data-id="element-1294">
                      ↵
                    </kbd>
                    <span data-id="element-1295">to select</span>
                  </div>
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}